import { Extension } from "../../../../../../../types/base";
import {
  TCopilot,
  TCopilotNotePopulationStatus,
} from "../../../../../../../types/copilot";

export const COPILOT_HUMAN_EDITED_URL =
  "https://example.com/fhir/StructureDefinition/human-edited";

const HUMAN_EDITED_BASE =
  "https://medial.health/fhir/StructureDefinition/human-edited";

export type CopilotHumanEditedSection =
  | "chief-complaint"
  | "hpi"
  | "vitals"
  | "allergies"
  | "medical-history"
  | "family-history"
  | "hospitalizations"
  | "procedures"
  | "social-history"
  | "sexual-history"
  | "medications"
  | "immunizations"
  | "review-of-systems"
  | "physical-assessment"
  | "diagnoses"
  | "plan"
  | "history";

/** Legacy allergy URLs — kept for backward compatibility with backend checks. */
export const ALLERGIES_EDITED_URL = `${HUMAN_EDITED_BASE}-allergies`;
export const ALLERGIES_EDITED_AT_URL = `${HUMAN_EDITED_BASE}-allergies-at`;

type SectionExtensionUrls = {
  flag: string;
  at: string;
};

const SECTION_EXTENSION_URLS: Record<
  CopilotHumanEditedSection,
  SectionExtensionUrls
> = {
  "chief-complaint": {
    flag: `${HUMAN_EDITED_BASE}-chief-complaint`,
    at: `${HUMAN_EDITED_BASE}-chief-complaint-at`,
  },
  hpi: {
    flag: `${HUMAN_EDITED_BASE}-hpi`,
    at: `${HUMAN_EDITED_BASE}-hpi-at`,
  },
  vitals: {
    flag: `${HUMAN_EDITED_BASE}-vitals`,
    at: `${HUMAN_EDITED_BASE}-vitals-at`,
  },
  allergies: {
    flag: ALLERGIES_EDITED_URL,
    at: ALLERGIES_EDITED_AT_URL,
  },
  "medical-history": {
    flag: `${HUMAN_EDITED_BASE}-medical-history`,
    at: `${HUMAN_EDITED_BASE}-medical-history-at`,
  },
  "family-history": {
    flag: `${HUMAN_EDITED_BASE}-family-history`,
    at: `${HUMAN_EDITED_BASE}-family-history-at`,
  },
  hospitalizations: {
    flag: `${HUMAN_EDITED_BASE}-hospitalizations`,
    at: `${HUMAN_EDITED_BASE}-hospitalizations-at`,
  },
  procedures: {
    flag: `${HUMAN_EDITED_BASE}-procedures`,
    at: `${HUMAN_EDITED_BASE}-procedures-at`,
  },
  "social-history": {
    flag: `${HUMAN_EDITED_BASE}-social-history`,
    at: `${HUMAN_EDITED_BASE}-social-history-at`,
  },
  "sexual-history": {
    flag: `${HUMAN_EDITED_BASE}-sexual-history`,
    at: `${HUMAN_EDITED_BASE}-sexual-history-at`,
  },
  medications: {
    flag: `${HUMAN_EDITED_BASE}-medications`,
    at: `${HUMAN_EDITED_BASE}-medications-at`,
  },
  immunizations: {
    flag: `${HUMAN_EDITED_BASE}-immunizations`,
    at: `${HUMAN_EDITED_BASE}-immunizations-at`,
  },
  "review-of-systems": {
    flag: `${HUMAN_EDITED_BASE}-review-of-systems`,
    at: `${HUMAN_EDITED_BASE}-review-of-systems-at`,
  },
  "physical-assessment": {
    flag: `${HUMAN_EDITED_BASE}-physical-assessment`,
    at: `${HUMAN_EDITED_BASE}-physical-assessment-at`,
  },
  diagnoses: {
    flag: `${HUMAN_EDITED_BASE}-diagnoses`,
    at: `${HUMAN_EDITED_BASE}-diagnoses-at`,
  },
  plan: {
    flag: `${HUMAN_EDITED_BASE}-plan`,
    at: `${HUMAN_EDITED_BASE}-plan-at`,
  },
  history: {
    flag: `${HUMAN_EDITED_BASE}-history`,
    at: `${HUMAN_EDITED_BASE}-history-at`,
  },
};

/** Maps EditableSection `field` props to human-edit section keys. */
export const NOTE_FIELD_TO_HUMAN_EDITED_SECTION: Record<
  string,
  CopilotHumanEditedSection
> = {
  chiefComplaint: "chief-complaint",
  hpi: "hpi",
  history: "history",
  reviewOfSystems: "review-of-systems",
  physicalAssessment: "physical-assessment",
  diagnoses: "diagnoses",
  plan: "plan",
};

/** Maps history subsection keys used in extractHistoryText to section keys. */
export const HISTORY_SUBSECTION_TO_HUMAN_EDITED_SECTION: Record<
  string,
  CopilotHumanEditedSection
> = {
  medical: "medical-history",
  family: "family-history",
  hospitalizations: "hospitalizations",
  procedures: "procedures",
  social: "social-history",
  sexual: "sexual-history",
  medications: "medications",
  immunizations: "immunizations",
};

const ALL_SECTION_FLAG_URLS = new Set(
  Object.values(SECTION_EXTENSION_URLS).map((urls) => urls.flag),
);

/** Sections to clear + populationStatus key after a successful Accept. */
export const POPULATION_FIELD_TO_HUMAN_EDITED_SECTIONS: Record<
  keyof TCopilotNotePopulationStatus,
  CopilotHumanEditedSection[]
> = {
  chiefComplaint: ["chief-complaint"],
  hpi: ["hpi"],
  vitals: ["vitals"],
  allergies: ["allergies"],
  familyHistory: ["family-history"],
  socialHistory: ["social-history"],
  sexualHistory: ["sexual-history"],
  immunizations: ["immunizations"],
  procedures: ["procedures"],
  diagnoses: ["diagnoses"],
  physicalAssessment: ["physical-assessment"],
  reviewOfSystems: ["review-of-systems"],
  hospitalization: ["hospitalizations"],
  medicalHistory: ["medical-history"],
  medications: ["medications"],
  carePlans: ["plan"],
  prescriptions: ["plan"],
};

export const HISTORY_POPULATE_APPLY_CONFIG: Record<
  string,
  {
    sections: CopilotHumanEditedSection[];
    populationStatusField: keyof TCopilotNotePopulationStatus;
  }
> = {
  "medical history": {
    sections: ["medical-history"],
    populationStatusField: "medicalHistory",
  },
  "family history": {
    sections: ["family-history"],
    populationStatusField: "familyHistory",
  },
  hospitalizations: {
    sections: ["hospitalizations"],
    populationStatusField: "hospitalization",
  },
  procedures: {
    sections: ["procedures"],
    populationStatusField: "procedures",
  },
  "social history": {
    sections: ["social-history"],
    populationStatusField: "socialHistory",
  },
  "sexual history": {
    sections: ["sexual-history"],
    populationStatusField: "sexualHistory",
  },
  medications: {
    sections: ["medications"],
    populationStatusField: "medications",
  },
  immunizations: {
    sections: ["immunizations"],
    populationStatusField: "immunizations",
  },
};

const SECTION_ACCEPT_TOOLTIP_OVERRIDES: Partial<
  Record<CopilotHumanEditedSection, string>
> = {
  allergies:
    "Will replace reactions on the chart for matching allergens, and void allergens you removed (clinician-edited note)",
  medications:
    "Will void chart medications previously applied from this note that you removed. Does not mark meds as discontinued (clinician-edited note)",
  "medical-history":
    "Will inactivate problem-list items previously applied from this note that you removed (clinician-edited note)",
  immunizations:
    "Will void immunizations previously applied from this note that you removed (clinician-edited note)",
  procedures:
    "Will void procedures previously applied from this note that you removed (clinician-edited note)",
};

function getSectionExtensionUrls(
  section: CopilotHumanEditedSection,
): SectionExtensionUrls {
  return SECTION_EXTENSION_URLS[section];
}

function stripSectionExtensions(
  extensions: Extension[] | undefined,
  section: CopilotHumanEditedSection,
): Extension[] {
  const { flag, at } = getSectionExtensionUrls(section);
  return (extensions ?? []).filter(
    (extension) => extension.url !== flag && extension.url !== at,
  );
}

function hasGlobalHumanEditedExtension(
  extensions: Extension[] | undefined,
): boolean {
  return (
    extensions?.some(
      (extension) =>
        extension.url === COPILOT_HUMAN_EDITED_URL && extension.valueBoolean,
    ) ?? false
  );
}

export function resolveHumanEditedSectionFromNoteField(
  field: string,
): CopilotHumanEditedSection | null {
  return NOTE_FIELD_TO_HUMAN_EDITED_SECTION[field] ?? null;
}

export function resolveHumanEditedSectionFromHistorySubsection(
  subsection: string,
): CopilotHumanEditedSection | null {
  return HISTORY_SUBSECTION_TO_HUMAN_EDITED_SECTION[subsection] ?? null;
}

export function isSectionHumanEdited(
  copilot: TCopilot | null | undefined,
  section: CopilotHumanEditedSection,
): boolean {
  const { flag } = getSectionExtensionUrls(section);
  return (
    copilot?.extension?.some(
      (extension) => extension.url === flag && extension.valueBoolean,
    ) ?? false
  );
}

/** @deprecated Use isSectionHumanEdited(copilot, 'allergies') */
export function isAllergiesHumanEdited(copilot?: TCopilot | null): boolean {
  return isSectionHumanEdited(copilot, "allergies");
}

export function getSectionHumanEditedAt(
  copilot: TCopilot | null | undefined,
  section: CopilotHumanEditedSection,
): Date | null {
  const { at } = getSectionExtensionUrls(section);
  const value = copilot?.extension?.find(
    (extension) => extension.url === at,
  )?.valueString;
  if (!value) {
    return null;
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function getHumanEditedSections(
  copilot: TCopilot | null | undefined,
): CopilotHumanEditedSection[] {
  if (!copilot?.extension?.length) {
    return [];
  }

  return (
    Object.keys(SECTION_EXTENSION_URLS) as CopilotHumanEditedSection[]
  ).filter((section) => isSectionHumanEdited(copilot, section));
}

export function syncGlobalHumanEditedExtension(copilot: TCopilot): TCopilot {
  const extensions = copilot.extension ?? [];
  const hasAnySectionFlag = extensions.some(
    (extension) =>
      ALL_SECTION_FLAG_URLS.has(extension.url) && extension.valueBoolean,
  );

  if (hasAnySectionFlag) {
    return copilot;
  }

  return {
    ...copilot,
    extension: extensions.filter(
      (extension) => extension.url !== COPILOT_HUMAN_EDITED_URL,
    ),
  };
}

export function clearSectionsHumanEditedAfterApply(
  copilot: TCopilot,
  sections: CopilotHumanEditedSection[],
  options: {
    populationStatus?: Partial<TCopilotNotePopulationStatus>;
  } = {},
): TCopilot {
  const uniqueSections = [...new Set(sections)];
  let updated = uniqueSections.reduce(
    (current, section) =>
      isSectionHumanEdited(current, section)
        ? withoutSectionHumanEditedExtension(current, section)
        : current,
    copilot,
  );

  updated = syncGlobalHumanEditedExtension(updated);

  if (options.populationStatus) {
    updated = {
      ...updated,
      note: {
        ...updated.note,
        populationStatus: {
          ...updated.note?.populationStatus,
          ...options.populationStatus,
        },
      },
    };
  }

  return updated;
}

export function buildPopulationStatusUpdate(
  field: keyof TCopilotNotePopulationStatus,
): Partial<TCopilotNotePopulationStatus> {
  return { [field]: true };
}

export function isPopulationFieldAccepted(
  copilot: TCopilot | null | undefined,
  field: keyof TCopilotNotePopulationStatus,
): boolean {
  return copilot?.note?.populationStatus?.[field] === true;
}

/** Hide Accept after a successful apply; show again when the section is human-edited. */
export function shouldShowSectionAcceptButton(
  copilot: TCopilot | null | undefined,
  options: {
    populationStatusField?: keyof TCopilotNotePopulationStatus;
    sections: CopilotHumanEditedSection[];
  },
): boolean {
  const { populationStatusField, sections } = options;

  if (sections.some((section) => isSectionHumanEdited(copilot, section))) {
    return true;
  }

  if (
    populationStatusField &&
    isPopulationFieldAccepted(copilot, populationStatusField)
  ) {
    return false;
  }

  return true;
}

export function getSectionsForPopulationField(
  field: keyof TCopilotNotePopulationStatus,
): CopilotHumanEditedSection[] {
  return POPULATION_FIELD_TO_HUMAN_EDITED_SECTIONS[field] ?? [];
}

export function getSectionAcceptTooltip(
  copilot: TCopilot | null | undefined,
  section: CopilotHumanEditedSection,
  defaultMessage = "Add content from the note to the chart",
): string {
  if (!isSectionHumanEdited(copilot, section)) {
    return defaultMessage;
  }

  const override = SECTION_ACCEPT_TOOLTIP_OVERRIDES[section];
  if (override) {
    return override;
  }

  const editedAt = getSectionHumanEditedAt(copilot, section);
  if (editedAt) {
    return `Apply clinician-edited content to the chart (saved ${editedAt.toLocaleString()})`;
  }

  return "Apply clinician-edited content to the chart";
}

export function getPopulationFieldAcceptTooltip(
  copilot: TCopilot | null | undefined,
  field: keyof TCopilotNotePopulationStatus,
  defaultMessage = "Add content from the note to the chart",
): string {
  const sections = getSectionsForPopulationField(field);
  const editedSection = sections.find((section) =>
    isSectionHumanEdited(copilot, section),
  );

  if (!editedSection) {
    return defaultMessage;
  }

  return getSectionAcceptTooltip(copilot, editedSection, defaultMessage);
}

export function isCopilotHumanEdited(copilot?: TCopilot | null): boolean {
  if (!copilot?.extension?.length) {
    return false;
  }

  if (getHumanEditedSections(copilot).length > 0) {
    return true;
  }

  return hasGlobalHumanEditedExtension(copilot.extension);
}

export function withSectionHumanEditedExtension(
  copilot: TCopilot,
  section: CopilotHumanEditedSection,
): TCopilot {
  const withoutSectionFlags = stripSectionExtensions(
    copilot.extension,
    section,
  );
  const { flag, at } = getSectionExtensionUrls(section);

  return {
    ...copilot,
    extension: [
      ...withoutSectionFlags,
      ...(!hasGlobalHumanEditedExtension(withoutSectionFlags)
        ? [{ url: COPILOT_HUMAN_EDITED_URL, valueBoolean: true }]
        : []),
      { url: flag, valueBoolean: true },
      { url: at, valueString: new Date().toISOString() },
    ],
  };
}

/** @deprecated Use withSectionHumanEditedExtension(copilot, 'allergies') */
export function withAllergiesEditedExtension(copilot: TCopilot): TCopilot {
  return withSectionHumanEditedExtension(copilot, "allergies");
}

export function withoutSectionHumanEditedExtension(
  copilot: TCopilot,
  section: CopilotHumanEditedSection,
): TCopilot {
  return {
    ...copilot,
    extension: stripSectionExtensions(copilot.extension, section),
  };
}

/** @deprecated Use withoutSectionHumanEditedExtension(copilot, 'allergies') */
export function withoutAllergiesEditedExtension(copilot: TCopilot): TCopilot {
  return withoutSectionHumanEditedExtension(copilot, "allergies");
}
